import { useEffect, useState } from "react";
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { Pagination } from "./Pagination";
import { DataSiswa, PaginationData } from "@/types";
import { useDebounce } from "@/hooks/useDebounce";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Search, Settings2 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  paginationData: PaginationData<DataSiswa>;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  paginationData,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});

  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(
    searchParams.get("search") || "",
  );
  const debouncedQuery = useDebounce(searchQuery, 1000);
  const navigate = useNavigate();
  const location = useLocation();

  const statusValidasiParams = searchParams.get("statusValidasi");

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (debouncedQuery) {
      if (params.get("search") !== debouncedQuery) {
        params.delete("page");
      }

      if (
        params.get("search") !== debouncedQuery ||
        params.get("page") !== "1"
      ) {
        params.set("search", debouncedQuery);
        navigate(`${location.pathname}?${params.toString()}`, {
          replace: true,
        });
      }
    } else {
      if (params.has("search") || params.get("page") !== "1") {
        params.delete("search");
        navigate(`${location.pathname}?${params.toString()}`, {
          replace: true,
        });
      }
    }
  }, [debouncedQuery, navigate, location.pathname, location.search]);

  return (
    <div className="flex flex-col w-full rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 shadow-sm overflow-hidden">
      {/* --- TOOLBAR: SEARCH, TABS & COLUMNS --- */}
      <div className="flex flex-col xl:flex-row items-start xl:items-center justify-between gap-4 p-4 lg:p-5 border-b border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50">
        {/* Kiri: Search Input */}
        <div className="relative w-full sm:w-80 lg:max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-4 w-4 text-slate-400" />
          </div>
          <Input
            autoFocus
            placeholder="Cari nama lengkap, NIK, NISN, email..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            className="pl-10 bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-700 w-full focus-visible:ring-primary h-10 shadow-sm transition-shadow"
          />
        </div>

        {/* Kanan: Tabs & Columns Toggle */}
        <div className="flex items-center gap-3 w-full xl:w-auto overflow-x-auto pb-1 xl:pb-0 no-scrollbar">
          <Tabs
            value={statusValidasiParams || "semua"}
            onValueChange={(value) => {
              if (searchParams.has("statusValidasi")) {
                searchParams.delete("statusValidasi");
              }
              if (value !== "semua") {
                searchParams.append("statusValidasi", value);
              }
              navigate(`${location.pathname}?${searchParams.toString()}`);
            }}
            className="w-full sm:w-auto shrink-0"
          >
            <TabsList className="grid w-full grid-cols-4 rounded-full bg-slate-200/60 dark:bg-slate-800 p-1 h-10 min-w-[320px] sm:min-w-0">
              <TabsTrigger
                value="semua"
                className="rounded-full text-sm data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 shadow-none data-[state=active]:shadow-sm transition-all"
              >
                Semua
              </TabsTrigger>
              <TabsTrigger
                value="belum-valid"
                className="rounded-full text-sm data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 shadow-none data-[state=active]:shadow-sm transition-all"
              >
                Belum Valid
              </TabsTrigger>
              <TabsTrigger
                value="tidak-valid"
                className="rounded-full text-sm data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 shadow-none data-[state=active]:shadow-sm transition-all"
              >
                Tidak Valid
              </TabsTrigger>
              <TabsTrigger
                value="sudah-valid"
                className="rounded-full text-sm data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 shadow-none data-[state=active]:shadow-sm transition-all"
              >
                Valid
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="h-10 shrink-0 bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-700 shadow-sm"
              >
                <Settings2 className="h-4 w-4 mr-2 text-slate-500" />
                Kolom
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-[200px] dark:bg-slate-900 dark:border-slate-800"
            >
              <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Tampilkan Kolom
              </div>
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize cursor-pointer dark:focus:bg-slate-800"
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) =>
                        column.toggleVisibility(!!value)
                      }
                    >
                      {column.id.replace(/_/g, " ")}
                    </DropdownMenuCheckboxItem>
                  );
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* --- TABLE AREA --- */}
      <div className="w-full overflow-x-auto min-h-[400px]">
        <Table className="w-full">
          <TableHeader className="bg-slate-50 dark:bg-slate-900/80 sticky top-0 z-10 shadow-sm">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow
                key={headerGroup.id}
                className="border-b border-slate-200 dark:border-slate-800 hover:bg-transparent"
              >
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      className="h-12 px-4 text-slate-600 dark:text-slate-300 font-semibold whitespace-nowrap"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className="border-b border-slate-100 dark:border-slate-800/60 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="py-3 px-4">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={table.getAllColumns().length}
                  className="h-48 text-center text-slate-500 dark:text-slate-400"
                >
                  <div className="flex flex-col items-center justify-center">
                    <Search className="h-8 w-8 mb-3 opacity-20" />
                    <p className="font-medium text-slate-700 dark:text-slate-300">
                      Data tidak ditemukan
                    </p>
                    <p className="text-sm">
                      Tidak ada hasil yang cocok dengan pencarian atau filter
                      Anda.
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* --- FOOTER PAGINATION --- */}
      <div className="p-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/30">
        <Pagination table={table} paginationData={paginationData} />
      </div>
    </div>
  );
}
